import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination
} from '@mui/material';
import { Add, Search, Edit, Delete, Close } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../../config/api';

const PersonInCharge = () => {
  const { user } = useAuth();
  const [allRows, setAllRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ picName: '' });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errors, setErrors] = useState({ picName: false });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [dialogMessage, setDialogMessage] = useState({ text: '', type: '' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchAlert, setSearchAlert] = useState({ open: false, message: '' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [singleDeleteConfirmOpen, setSingleDeleteConfirmOpen] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);

  useEffect(() => {
    fetchPersonsInCharge();
  }, []);

  const fetchPersonsInCharge = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.PERSON_IN_CHARGE);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const formattedData = data.map(item => ({
        id: item.pic_id,
        picName: item.pic_name
      }));
      setAllRows(formattedData);
      setFilteredRows(formattedData);
    } catch (error) {
      console.error('Error fetching persons in charge:', error);
      setMessage({ text: 'Failed to load persons in charge. Please check if the server is running.', type: 'error' });
      setAllRows([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const rowToEdit = filteredRows.find(row => row.id === id);
    setFormData({ picName: rowToEdit.picName });
    setEditMode(true);
    setEditId(id);
    setMessage({ text: '', type: '' });
    setOpen(true);
  };

  const handleDeleteRow = (id) => {
    setDeleteRowId(id);
    setSingleDeleteConfirmOpen(true);
  };

  const handleSingleDeleteConfirm = async () => {
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(`${API_ENDPOINTS.PERSON_IN_CHARGE}/${deleteRowId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete person in charge');
      }

      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      await fetchPersonsInCharge();
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error deleting person in charge:', error);
      setMessage({ text: error.message || 'Failed to delete person in charge', type: 'error' });
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = paginatedRows.map((row) => row.id);
      setSelectedRows(newSelected);
      return;
    }
    setSelectedRows([]);
  };

  const handleRowSelect = (event, id) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }

    setSelectedRows(newSelected);
  };

  const isSelected = (id) => selectedRows.indexOf(id) !== -1;

  const handleChangePage = (event, newPage) => {
    setPaginationModel({ ...paginationModel, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) });
  };

  const paginatedRows = filteredRows.slice(
    paginationModel.page * paginationModel.pageSize,
    paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
  );

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setSearchAlert({ open: true, message: 'Please enter search text' });
      return;
    }

    setSearchLoading(true);
    setTimeout(() => {
      const filtered = allRows.filter(row =>
        row.picName && row.picName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRows(filtered);
      setSearchLoading(false);
    }, 500);
  };

  const handleShowAll = () => {
    setFilteredRows(allRows);
    setSearchTerm('');
  };

  const handleSave = async () => {
    setErrors({ picName: false });
    setDialogMessage({ text: '', type: '' });

    if (!formData.picName.trim()) {
      setErrors({ picName: true });
      setDialogMessage({ text: 'PIC Name is mandatory', type: 'error' });
      setTimeout(() => {
        document.querySelector(`input[name="picName"]`)?.focus();
      }, 100);
      return;
    }

    setSaveLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const url = editMode ? `${API_ENDPOINTS.PERSON_IN_CHARGE}/${editId}` : API_ENDPOINTS.PERSON_IN_CHARGE;
      const method = editMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          picName: formData.picName.trim(),
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${editMode ? 'update' : 'create'} person in charge`);
      }

      await fetchPersonsInCharge();
      setOpen(false);
      handleClear();
      setMessage({ text: `Person in charge ${editMode ? 'updated' : 'created'} successfully`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error saving person in charge:', error);
      setDialogMessage({ 
        text: error.message.includes('PIC') ? error.message : `Failed to ${editMode ? 'update' : 'save'} person in charge`, 
        type: 'error' 
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({ picName: '' });
    setErrors({ picName: false });
    setDialogMessage({ text: '', type: '' });
    setEditMode(false);
    setEditId(null);
  };

  const handleDeleteClick = () => {
    if (!selectedRows || selectedRows.length === 0) {
      setMessage({ text: 'Please select items to delete', type: 'warning' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const selectedIds = selectedRows || [];
    
    if (!selectedIds || selectedIds.length === 0) {
      setMessage({ text: 'No items selected for deletion', type: 'warning' });
      setDeleteConfirmOpen(false);
      return;
    }
    
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(API_ENDPOINTS.PERSON_IN_CHARGE, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selectionModel: selectedIds,
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete persons in charge');
      }

      const result = await response.json();
      setMessage({ text: result.message || 'Selected persons in charge deleted successfully', type: 'success' });
      setSelectedRows([]);
      await fetchPersonsInCharge();
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } catch (error) {
      console.error('Error deleting persons in charge:', error);
      setMessage({ text: error.message || 'Failed to delete persons in charge', type: 'error' });
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Person in Charge</Typography>

      {(loading || saveLoading || deleteLoading || searchLoading) && (
        <LinearProgress sx={{ mb: 2 }} />
      )}
      
      {searchAlert.open && (
        <Alert 
          severity="warning" 
          onClose={() => setSearchAlert({ open: false, message: '' })}
          sx={{ mb: 2 }}
        >
          {searchAlert.message}
        </Alert>
      )}
      
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button 
            variant="outlined" 
            startIcon={searchLoading ? <CircularProgress size={16} /> : <Search />} 
            onClick={handleSearch}
            disabled={searchLoading}
          >
            Search
          </Button>
          {filteredRows.length !== allRows.length && (
            <Button variant="outlined" onClick={handleShowAll}>
              Show All
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />} 
            onClick={handleDeleteClick}
            disabled={!selectedRows || selectedRows.length === 0 || deleteLoading}
          >
            Delete ({selectedRows?.length || 0})
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditMode(false); setEditId(null); setOpen(true); }}>
            Add Person in Charge
          </Button>
        </Box>
      </Box>

      {!loading && filteredRows && Array.isArray(filteredRows) && (
        <Paper sx={{ width: '100%', mb: 2, boxShadow: 2, borderRadius: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={selectedRows.length > 0 && selectedRows.length < paginatedRows.length}
                      checked={paginatedRows.length > 0 && paginatedRows.every(row => selectedRows.includes(row.id))}
                      onChange={handleSelectAllClick}
                      sx={{ color: 'white' }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
                    PIC Name
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#e3f2fd !important' }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(event) => handleRowSelect(event, row.id)}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        sx={{ fontSize: '13px', padding: '8px 16px' }}
                      >
                        {row.picName}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '8px 16px' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Edit
                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                            onClick={() => handleEdit(row.id)}
                          />
                          <Delete
                            sx={{ cursor: 'pointer', color: 'error.main' }}
                            onClick={() => handleDeleteRow(row.id)}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={paginationModel.pageSize}
            page={paginationModel.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              backgroundColor: '#f8f9fa',
              borderTop: '2px solid #e0e0e0',
              '& .MuiTablePagination-root': { color: '#666' }
            }}
          />
        </Paper>
      )}
      
      {loading && (
        <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      )}

      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editMode ? 'Edit Person in Charge' : 'Add Person in Charge'}
          <Close sx={{ cursor: 'pointer' }} onClick={() => { setOpen(false); handleClear(); }} />
        </DialogTitle>
        <DialogContent>
          {dialogMessage.text && (
            <Alert severity={dialogMessage.type} sx={{ mb: 2 }}>
              {dialogMessage.text}
            </Alert>
          )}
          <TextField
            fullWidth
            name="picName"
            label="PIC Name"
            value={formData.picName}
            onChange={(e) => setFormData({ ...formData, picName: e.target.value })}
            error={errors.picName}
            helperText={errors.picName ? 'PIC Name is required' : ''}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} disabled={saveLoading}>Clear</Button>
          <Button onClick={() => { setOpen(false); handleClear(); }} disabled={saveLoading}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedRows?.length || 0} selected person{(selectedRows?.length || 0) > 1 ? 's' : ''} in charge?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={singleDeleteConfirmOpen} onClose={() => setSingleDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this person in charge?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleSingleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonInCharge;